import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { PANListResponse } from "src/models/PAN.model";
import {
  resetState,
  setIsTableLoading,
  setItems,
  setTotalItems,
} from "src/redux/slices/PANApplication/PANFormFillPendingApplicationSlice";
import { AppDispatch, RootState } from "src/redux/store";
import {
  useChangePANStatusMutation,
  useGetPanFormFillPendingApplicationsQuery,
  useSkipPANFormFillMutation,
} from "src/services/PANService";
import { formatDateAndTime } from "src/utils/dateAndTime";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import { showToast } from "src/utils/toaster/showToast";
import PANFormFillPendingApplicationListing from "./PANFormFillPendingApplicationListing";
import { getColumns } from "src/utils/auth/getColumns";
import AuthHOC from "src/userAccess/AuthHOC";
import { AccessAction } from "src/utils/Enums/AccessAction";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";

const paramList = [
  "category",
  "name",
  "email",
  "dob",
  "adhaarNumber",
  "mobileNumber",
  "passportPhotoUrl",
  "signaturePhotoUrl",
  "panFormFrontPhotoUrl",
  "panFormBackPhotoUrl",
  "adhaarFrontPhotoUrl",
  "adhaarBackPhotoUrl",
  "otherDocuments",
  "comment",
  "distributorCode",
  "appliedBy",
  "appliedAs",
  "txnId",
  "payementDetails",
  "appliedFrom",
  "paymentCategory",
  "version",
  "panNumber",
  "acknowledgementNumber",
  "status",
  "assignedTo",
  "assignedBy",
];

const PANFormFillPendingApplicationListingWrapper = () => {
  const navigate = useNavigate();
  // Dispatch Hook
  const dispatch = useDispatch<AppDispatch>();

  // Slice
  const PANFormFillPendingApplicationState: any = useSelector(
    (state: RootState) => state.PANFormFillPendingApplication
  );
  const {
    items,
    searchValue,
    rowsPerPage,
    page,
    filterBy,
    dateFilter,
    orderByValue,
  } = PANFormFillPendingApplicationState;
  const [changeStatus, changeStatusInfo] = useChangePANStatusMutation();
  // Loading is tracked by isBulkSkipping: the whole run is one action to the
  // admin, not one spinner per request.
  const [skipFormFill] = useSkipPANFormFillMutation();
  const [applicationId, setApplicationId] = useState("");
  // Rows ticked in the selection column, held as whole rows (that is what
  // ATMTable hands back) so the failure toast can name them by srn.
  const [selectedRows, setSelectedRows] = useState<PANListResponse[]>([]);
  const [isBulkSkipping, setIsBulkSkipping] = useState(false);
  React.useEffect(() => {
    dispatch(resetState());

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get Data Query
  const { data, isFetching, isLoading } = useGetPanFormFillPendingApplicationsQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: paramList,
    page: page,
    filterBy: filterBy,
    dateFilter: dateFilter,
    orderBy: "createdDateToShow",
    orderByValue: orderByValue,
    isPaginationRequired: true,
  });

  /*
    A selection only ever means "these rows, on this page". Paging, searching,
    sorting or filtering swaps the rows out from under it, so the ticks are
    dropped rather than left pointing at applications the admin can no longer
    see.
  */
  React.useEffect(() => {
    setSelectedRows([]);
  }, [page, rowsPerPage, searchValue, orderByValue, dateFilter, filterBy]);

  // Setting data
  React.useEffect(() => {
    if (!isFetching && !isLoading) {
      dispatch(setIsTableLoading(false));
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
    } else {
      dispatch(setIsTableLoading(true));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isFetching, data]);

  /*
    Read once per render instead of inside renderCell: AuthHOC only reads
    localStorage, and the answer is identical for every row.

    The two grants are deliberately separate. "May send an application to print
    without filling the form" is a different permission from "may fill the
    form", and the API enforces the same split — /pan-app/skip-form-fill exists
    as its own route purely so it can carry SKIP_FORM_FILL.
  */
  const canStartFormFill = Boolean(
    AuthHOC({
      resultType: "BOOLEAN",
      forApplicationStatus: true,
      applicationName: "PAN",
      applicationStatus: "FORM_FILL_IN_PROGRESS",
    })
  );
  const canSkipFormFill = Boolean(
    AuthHOC({
      resultType: "BOOLEAN",
      type: "ACTION",
      moduleName: "PAN_APPLICATIONS",
      action: AccessAction.SKIP_FORM_FILL,
    })
  );

  /*
    Skips run one request at a time. There is no batch endpoint behind this —
    each call re-routes a single application for print — and firing the whole
    selection at once would put an unbounded burst on that route. A partial run
    is reported honestly rather than collapsed into one success or failure.
  */
  const handleBulkSkip = () => {
    showConfirmationDialog({
      title: "Heads Up",
      text: `Skip the PAN form for ${selectedRows.length} selected application${
        selectedRows.length > 1 ? "s" : ""
      } and send ${
        selectedRows.length > 1 ? "them" : "it"
      } straight for printing ?`,
      icon: "question",
      showCancelButton: true,
      next: async (result) => {
        if (!result.isConfirmed) {
          return;
        }

        setIsBulkSkipping(true);

        let skipped = 0;
        const failures: string[] = [];

        for (const row of selectedRows) {
          const res: any = await skipFormFill({
            id: row?._id,
            // Plain JSON, not FormData: the bypass carries no file, and the API
            // route has no multipart parser.
            body: { requestedStatus: "GENERATE" },
          });

          if (res?.error) {
            failures.push(
              `${row?.srn}: ${res.error?.data?.message || "Request failed"}`
            );
          } else if (res?.data?.status) {
            skipped += 1;
          } else {
            failures.push(`${row?.srn}: ${res?.data?.message}`);
          }
        }

        setIsBulkSkipping(false);
        setSelectedRows([]);

        if (skipped) {
          showToast(
            "success",
            `${skipped} application${skipped > 1 ? "s" : ""} sent for printing.`
          );
        }
        if (failures.length) {
          showToast(
            "error",
            `${failures.length} could not be skipped — ${failures[0]}`
          );
        }
      },
    });
  };

  const columns: columnTypes[] = [
    {
      hidden: !canStartFormFill,
      field: "",
      noAuthRequired: true,
      headerName: "Move To",
      flex: "flex-[1_1_0%]",
      renderCell: (row: PANListResponse) => (
        <div className={"py-2"}>
          <ATMLoadingButton
            loadingText="Processing..."
            className="bg-status-formfillprogress w-32 p-0 py-[6px]"
            type="button"
            isLoading={applicationId === row?._id && changeStatusInfo?.isLoading}
            disabled={applicationId === row?._id && changeStatusInfo?.isLoading}
            onClick={(e) => {
              e.stopPropagation();
              showConfirmationDialog({
                title: "Heads Up",
                text: "Are you sure want to start Form Fill for this application ?",
                icon: "question",
                showCancelButton: true,
                next: (result) => {
                  if (result.isConfirmed) {
                    setApplicationId(row?._id);
                    const formData = new FormData();
                    formData.append("requestedStatus", "FORM_FILL_IN_PROGRESS");
                    changeStatus({
                      id: row._id,
                      body: formData,
                    }).then((res: any) => {
                      if (res.error) {
                        showToast("error", res.error.data?.message);
                      } else {
                        if (res.data?.status) {
                          showToast("success", res.data?.message);
                          navigate(`/pan/FORM_FILL_PENDING`);
                        } else {
                          showToast("error", res.data?.message);
                        }
                      }
                    });
                  }
                },
              });
            }}
          >
            Start Form Fill
          </ATMLoadingButton>
        </div>
      ),
    },

    {
      field: "createdDateToShow",
      headerName: "Date - Time",
      flex: "flex-[1_1_0%]",
      renderCell: (row: PANListResponse) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatDateAndTime(row?.createdDateToShow, "DD MMM yyyy")}
          </div>
          <div className="text-[13px] font-medium text-slate-400">
            {formatDateAndTime(row?.createdDateToShow, "hh:mm A")}
          </div>
        </div>
      ),
    },
    {
      field: "srn",
      headerName: "SRN",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: PANListResponse) => {
        return (
          <span className="text-primary-main text-[13px] text-ellipsis overflow-hidden">
            {" "}
            {row.srn}{" "}
          </span>
        );
      },
    },
    {
      field: "name",
      headerName: "Name",
      flex: "flex-[1_1_0%]",
      renderCell: (row: PANListResponse) => (
        <span className="text-ellipsis overflow-hidden"> {row.name} </span>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: PANListResponse) => {
        return (
          <span className="text-ellipsis overflow-hidden">
            {" "}
            {row.email || "N/A"}{" "}
          </span>
        );
      },
    },
    {
      field: "mobileNumber",
      headerName: "Mobile",
      flex: "flex-[1_1_0%]",
    },
    {
      noAuthRequired: true,
      field: "distributorCode",
      headerName: "SJBT  Code",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: PANListResponse) => {
        return <span> {row.distributorCode || "N/A"} </span>;
      },
    },
    {
      field: "appliedByNumber",
      headerName: "Applied By (Mob. No.)",
      flex: "flex-[1.5_1.5_0%]",
      renderCell: (row: PANListResponse) => {
        return <span> {row.appliedByNumber || "N/A"} </span>;
      },
    },
  ];

  return (
    <>
      <PANFormFillPendingApplicationListing
        columns={getColumns(columns, "PAN_APPLICATIONS")}
        rows={items}
        // The selection column and the bulk action ride on the same grant: no
        // SKIP_FORM_FILL, no checkboxes to tick.
        canSkipFormFill={canSkipFormFill}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        isBulkSkipping={isBulkSkipping}
        onBulkSkip={handleBulkSkip}
      />
    </>
  );
};

export default PANFormFillPendingApplicationListingWrapper;
